import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '../../components/UI/card';
import { Button } from '../../components/UI/button';
import { Badge } from '../../components/UI/badge';
import { Loader2, CreditCard, Download, CheckCircle, XCircle, Clock, FileText } from 'lucide-react';
import PaymentMethodSelector from '../../components/Payment/PaymentMethodSelector';
import PaymentForm from '../../components/Payment/PaymentForm';
import { useToast } from '../../hooks/use-toast';

/**
 * PAGE: PAIEMENT SELF-SERVICE
 * 
 * Permet aux mutualistes de payer leurs propres cotisations
 */

const PaymentSelfService = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [contributions, setContributions] = useState([]);
  const [selectedContribution, setSelectedContribution] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);

  // Charger les cotisations en attente et l'historique
  useEffect(() => {
    loadContributions();
    loadPaymentHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadContributions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      // Récupérer les cotisations en attente ou en retard
      const response = await axios.get(
        `${API_URL}/contributions/user?status=PENDING,OVERDUE`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setContributions(response.data.contributions || []);
    } catch (error) {
      console.error('Erreur chargement cotisations:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible de charger vos cotisations'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectContribution = (contribution) => {
    setSelectedContribution(contribution);
    setShowPaymentForm(true);
  };

  const handleSelectPaymentMethod = (method) => {
    setPaymentMethod(method);
  };

  const handleSubmitPayment = async (formData) => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('accessToken');

      const payload = {
        contributionId: selectedContribution.id,
        paymentMethod,
        externalRef: formData.externalRef || null,
        notes: formData.notes || null
      };

      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      const response = await axios.post(
        `${API_URL}/payments/self-service`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: 'Succès',
        description: 'Paiement enregistré avec succès'
      });

      // Réinitialiser et recharger
      setShowPaymentForm(false);
      setSelectedContribution(null);
      setPaymentMethod(null);
      loadContributions();

      // Rediriger vers la page de confirmation avec le reçu
      navigate(`/payments/${response.data.data.id}`);
    } catch (error) {
      console.error('Erreur paiement:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.response?.data?.message || 'Erreur lors du paiement'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelPayment = () => {
    setShowPaymentForm(false);
    setSelectedContribution(null);
    setPaymentMethod(null);
  };

  const loadPaymentHistory = async () => {
    try {
      setLoadingPayments(true);
      const token = localStorage.getItem('accessToken');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      const response = await axios.get(
        `${API_URL}/payments?status=COMPLETED&limit=10`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setPayments(response.data.data || []);
    } catch (error) {
      console.error('Erreur chargement historique:', error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      CARTE_BANCAIRE: 'Carte bancaire',
      ORANGE_MONEY: 'Orange Money',
      MTN_MONEY: 'MTN Money',
      MOOV_MONEY: 'Moov Money',
      WAVE: 'Wave',
      ESPECES: 'Espèces',
      VIREMENT: 'Virement',
      MOBILE_MONEY: 'Mobile Money'
    };
    return labels[method] || method;
  };

  const handleDownloadDuplicate = async (paymentId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      // Télécharger le duplicata
      const response = await axios.get(
        `${API_URL}/payments/${paymentId}/receipt-duplicate`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob' // Important pour télécharger un fichier
        }
      );

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `duplicata-recu-${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Duplicata téléchargé',
        description: 'Le duplicata de reçu a été téléchargé avec succès',
      });
    } catch (error) {
      console.error('Erreur téléchargement duplicata:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de télécharger le duplicata',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'PENDING') {
      return <Badge variant="warning"><Clock className="w-3 h-3 mr-1" /> En attente</Badge>;
    } else if (status === 'OVERDUE') {
      return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> En retard</Badge>;
    } else if (status === 'PAID') {
      return <Badge variant="success"><CheckCircle className="w-3 h-3 mr-1" /> Payé</Badge>;
    }
    return null;
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div>
          <h1 className="text-2xl font-bold text-primary-900 mb-2">
            Mes paiements
          </h1>
          <p className="text-gray-600">
            Payez vos cotisations en ligne en toute sécurité
          </p>
        </div>
      </div>

      {/* Formulaire de paiement (modal) */}
      {showPaymentForm && selectedContribution && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle className="flex items-center">
              <CreditCard className="w-5 h-5 mr-2" />
              Paiement de cotisation
            </CardTitle>
            <CardDescription>
              {selectedContribution.policy.subscription.offer.name} - {selectedContribution.policy.policyNumber}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Récapitulatif */}
            <div className="bg-muted p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm">Montant à payer</span>
                <span className="text-2xl font-bold text-primary">
                  {formatAmount(selectedContribution.amount)}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Date d'échéance</span>
                <span>{formatDate(selectedContribution.dueDate)}</span>
              </div>
            </div>

            {/* Sélection du mode de paiement */}
            {!paymentMethod && (
              <PaymentMethodSelector
                onSelect={handleSelectPaymentMethod}
                allowedMethods={[
                  'CARTE_BANCAIRE',
                  'ORANGE_MONEY',
                  'MTN_MONEY',
                  'MOOV_MONEY',
                  'WAVE',
                  'ESPECES'
                ]}
              />
            )}

            {/* Formulaire de paiement */}
            {paymentMethod && (
              <PaymentForm
                paymentMethod={paymentMethod}
                amount={selectedContribution.amount}
                onSubmit={handleSubmitPayment}
                onCancel={handleCancelPayment}
                submitting={submitting}
              />
            )}

            {/* Boutons d'action */}
            {!paymentMethod && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleCancelPayment}
                  className="flex-1"
                >
                  Annuler
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Liste des cotisations */}
      {!showPaymentForm && (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {contributions.map((contribution) => (
                <Card key={contribution.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">
                        {contribution.policy.subscription.offer.name}
                      </CardTitle>
                      {getStatusBadge(contribution.status)}
                    </div>
                    <CardDescription>
                      Police N° {contribution.policy.policyNumber}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Montant */}
                    <div className="flex justify-between items-center py-2 border-t border-b">
                      <span className="text-sm text-muted-foreground">Montant</span>
                      <span className="text-xl font-bold text-primary">
                        {formatAmount(contribution.amount)}
                      </span>
                    </div>

                    {/* Date d'échéance */}
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Échéance</span>
                      <span className={contribution.status === 'OVERDUE' ? 'text-red-600 font-semibold' : ''}>
                        {formatDate(contribution.dueDate)}
                      </span>
                    </div>

                    {/* Mode de paiement */}
                    {contribution.paymentMethod && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">Mode de paiement</span>
                        <span>{contribution.paymentMethod}</span>
                      </div>
                    )}

                    {/* Bouton payer */}
                    <Button
                      onClick={() => handleSelectContribution(contribution)}
                      className="w-full"
                      variant={contribution.status === 'OVERDUE' ? 'destructive' : 'default'}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Payer maintenant
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
        </>
      )}

      {/* Historique des paiements */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Historique des paiements</CardTitle>
              <CardDescription>
                Vos 10 derniers paiements effectués
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/payments/history')}
            >
              <Download className="w-4 h-4 mr-2" />
              Voir tout
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingPayments ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-8">
              <CreditCard className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <p className="text-sm text-muted-foreground">
                Aucun paiement effectué pour le moment
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {payments.map((payment) => (
                <div
                  key={payment.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {formatAmount(payment.amount)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {getPaymentMethodLabel(payment.paymentMethod)} • {formatDate(payment.createdAt)}
                      </p>
                      {payment.receiptNumber && (
                        <p className="text-xs text-gray-500 font-mono">
                          Reçu: {payment.receiptNumber}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Badge variant="success">Complété</Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadDuplicate(payment.id)}
                      title="Télécharger le duplicata de reçu"
                      className="flex items-center space-x-1"
                    >
                      <FileText className="w-4 h-4" />
                      <span className="hidden sm:inline">Duplicata</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSelfService;
